import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { BOOKING } from '@/constants/testIds';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';

const BookingInquiryModal = ({ artist, open, onOpenChange }) => {
  const [form, setForm] = useState({
    event_type: '', event_date: '', location: '', budget: '',
    message: '', contact_email: '', contact_phone: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/bookings', { artist_id: artist.artist_id, ...form });
      toast.success('Booking request sent!', {
        description: `${artist.stage_name} will respond soon.`,
      });
      onOpenChange(false);
      setForm({ event_type: '', event_date: '', location: '', budget: '', message: '', contact_email: '', contact_phone: '' });
    } catch (err) {
      toast.error('Could not send inquiry', {
        description: err?.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={BOOKING.modal} className="glass-strong border-white/10 text-slate-100 max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Book <span className="rainbow-text">{artist?.stage_name}</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Share your event details. The artist (or their AI manager) will reply with a quote.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Event type</Label>
              <Input value={form.event_type} onChange={update('event_type')} required placeholder="Wedding, Concert, Brand Shoot…" className="bg-white/5 border-white/10 mt-1" data-testid={BOOKING.eventTypeInput} />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Event date</Label>
              <Input type="date" value={form.event_date} onChange={update('event_date')} required className="bg-white/5 border-white/10 mt-1" data-testid={BOOKING.eventDateInput} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Location</Label>
              <Input value={form.location} onChange={update('location')} required placeholder="City, Country" className="bg-white/5 border-white/10 mt-1" data-testid={BOOKING.locationInput} />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Budget (optional)</Label>
              <Input value={form.budget} onChange={update('budget')} placeholder="₹2-5L" className="bg-white/5 border-white/10 mt-1" data-testid={BOOKING.budgetInput} />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Your message</Label>
            <Textarea value={form.message} onChange={update('message')} required rows={4} placeholder="Tell the artist about your event…" className="bg-white/5 border-white/10 mt-1" data-testid={BOOKING.messageInput} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Email</Label>
              <Input type="email" value={form.contact_email} onChange={update('contact_email')} required className="bg-white/5 border-white/10 mt-1" data-testid={BOOKING.emailInput} />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Phone (optional)</Label>
              <Input value={form.contact_phone} onChange={update('contact_phone')} className="bg-white/5 border-white/10 mt-1" data-testid={BOOKING.phoneInput} />
            </div>
          </div>
          <Button type="submit" disabled={submitting} className="w-full btn-rainbow text-white border-0 rounded-xl py-6 text-base" data-testid={BOOKING.submitButton}>
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <><Sparkles className="h-4 w-4 mr-2" /> Send inquiry</>}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingInquiryModal;
